import { JSONContent } from '@tiptap/core';
import React, { useRef, useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Icon,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SimpleTooltip,
  Skeleton,
  Switch,
  Text,
  Textarea,
  cn,
  useToast,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import {
  DynamicArtifactTemplate,
  hydrateDynamicArtifactTemplate,
} from '@eluve/llm-outputs';
import { useIsEluveAdmin, useTenantIdFromParams } from '@eluve/session-helpers';

import {
  GenerationProgressLog,
  buildGenerationStatusMessages,
} from './GenerationProgressLog';
import { ModelPreset, ModelPresetSlider } from './ModelPresetSlider';
import { PreviewGenerationStats } from './PreviewGenerationStats';
import { TemplatePreviewArtifact } from './TemplatePreviewArtifact';
import {
  TemplateSectionsEditor,
  TemplateSectionsEditorHandle,
} from './prompt-editor/TemplateSectionsEditor';
import { docToDynamicArtifact } from './prompt-editor/templateSectionsDoc';
import { SAMPLE_TRANSCRIPTS } from './sampleTranscripts';

const CUSTOM_SAMPLE_ID = 'custom';

// Private Components

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text
    inline
    size="xs"
    weight="medium"
    className="tracking-wide text-text-tertiary uppercase"
  >
    {children}
  </Text>
);

const PreviewEmptyState: React.FC<{
  onGenerate: () => void;
  disabled: boolean;
}> = ({ onGenerate, disabled }) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-5 py-16">
      <Icon name="FileText" className="size-10 text-text-tertiary/60" />
      <Text color="secondary" size="md" className="max-w-72 text-center">
        {t('outputTemplates.workspace.previewEmptyState')}
      </Text>
      <Button
        onClick={onGenerate}
        disabled={disabled}
        className="bg-bg-indigo-strong-primary text-white hover:bg-bg-indigo-strong-primary/90"
      >
        <Icon name="Play" size="sm" />
        {t('outputTemplates.workspace.generatePreview')}
      </Button>
    </div>
  );
};

const PreviewLoadingState: React.FC = () => (
  <div className="flex flex-1 flex-col gap-4 pt-2">
    {[0, 1, 2].map((section) => (
      <div key={section} className="flex flex-col gap-2">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-3 w-full" />
        <Skeleton className="h-3 w-5/6" />
        <Skeleton className="h-3 w-2/3" />
      </div>
    ))}
  </div>
);

// Public Components

export interface TemplateBundleWorkspaceProps {
  templateName: string;
  onTemplateNameChange: (name: string) => void;
  /** Template description, edited under Additional settings */
  description: string;
  onDescriptionChange: (description: string) => void;
  /** Sections document loaded into the editor */
  initialDoc: JSONContent;
  /**
   * Remounts the sections editor (and reloads initialDoc) when it
   * changes — used to switch between versions on the edit page
   */
  editorKey?: React.Key;
  /** Called with the full document JSON on every edit */
  onDocChange: (doc: JSONContent) => void;
  onPresetSelected?: (preset: ModelPreset) => void;
  /** Preselects the model preset backed by this llm_model_args id */
  initialModelArgsId?: string;
  /** Locks all editing controls (non-owner viewing a template) */
  readOnly?: boolean;
}

/**
 * The template authoring workspace shared by the new-template and
 * edit-template pages: name input, model preset slider, advanced
 * settings, the section-based prompt editor, and the live preview panel
 * (sample transcript library + generation via the preview endpoint).
 */
export const TemplateBundleWorkspace: React.FC<
  TemplateBundleWorkspaceProps
> = ({
  templateName,
  onTemplateNameChange,
  description,
  onDescriptionChange,
  initialDoc,
  editorKey,
  onDocChange,
  onPresetSelected,
  initialModelArgsId,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const apiClient = useApiClient();
  const tenantId = useTenantIdFromParams();
  const toast = useToast();

  const [selectedPreset, setSelectedPreset] = useState<ModelPreset | null>(
    null,
  );

  const [sampleId, setSampleId] = useState(SAMPLE_TRANSCRIPTS[0]!.id);
  const [customTranscript, setCustomTranscript] = useState('');
  const [customDraft, setCustomDraft] = useState('');
  const [isCustomDialogOpen, setIsCustomDialogOpen] = useState(false);

  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [allowGroups, setAllowGroups] = useState(false);

  const docRef = useRef<JSONContent>(initialDoc);
  const sectionsEditorRef = useRef<TemplateSectionsEditorHandle>(null);
  const isEluveAdmin = useIsEluveAdmin();
  const [showSchema, setShowSchema] = useState(false);
  // When the editor is remounted with a different document (version
  // switch), the working copy must follow
  const lastEditorKey = useRef(editorKey);
  if (lastEditorKey.current !== editorKey) {
    lastEditorKey.current = editorKey;
    docRef.current = initialDoc;
  }

  const [isGenerating, setIsGenerating] = useState(false);
  const [statusMessages, setStatusMessages] = useState<string[]>([]);
  const [previewTemplate, setPreviewTemplate] =
    useState<DynamicArtifactTemplate | null>(null);
  // Generation time + token usage for the footer, kept with previewTemplate
  const [previewStats, setPreviewStats] = useState<{
    durationMs: number;
    inputTokens?: number;
    outputTokens?: number;
  } | null>(null);
  // Render-phase reset (the standard derived-state pattern): a version
  // switch must not keep showing the previous version's preview
  const lastPreviewEditorKey = useRef(editorKey);
  if (lastPreviewEditorKey.current !== editorKey) {
    lastPreviewEditorKey.current = editorKey;
    setPreviewTemplate(null);
    setPreviewStats(null);
  }

  const selectedTranscript =
    sampleId === CUSTOM_SAMPLE_ID
      ? customTranscript
      : (SAMPLE_TRANSCRIPTS.find((s) => s.id === sampleId)?.transcript ?? '');

  const handleSampleChange = (value: string) => {
    if (value === CUSTOM_SAMPLE_ID) {
      // Selection only commits once the user saves their pasted text
      setCustomDraft(customTranscript);
      setIsCustomDialogOpen(true);
      return;
    }
    setSampleId(value);
  };

  const saveCustomTranscript = () => {
    setCustomTranscript(customDraft);
    setSampleId(CUSTOM_SAMPLE_ID);
    setIsCustomDialogOpen(false);
  };

  const handlePresetSelected = (preset: ModelPreset) => {
    setSelectedPreset(preset);
    onPresetSelected?.(preset);
  };

  const handleGenerate = async () => {
    if (!tenantId || !selectedTranscript.trim()) {
      return;
    }

    const template = docToDynamicArtifact({
      doc: docRef.current,
      name: templateName || 'New Template',
    });

    setStatusMessages(buildGenerationStatusMessages(template, t));
    setIsGenerating(true);
    const startedAt = performance.now();
    try {
      const response = await apiClient.llm.previewTemplateBundle({
        params: { tenantId },
        body: {
          outputTemplate: template,
          modelPresetId: selectedPreset?.id,
          transcript: selectedTranscript,
        },
      });
      const wallClockMs = performance.now() - startedAt;

      if (
        response.status === 201 &&
        response.body.success &&
        response.body.output
      ) {
        const hydrated = hydrateDynamicArtifactTemplate(
          template,
          response.body.output as Record<string, unknown>,
        );
        setPreviewTemplate(hydrated);
        setPreviewStats({
          durationMs: response.body.usage?.durationMs ?? wallClockMs,
          inputTokens: response.body.usage?.inputTokens,
          outputTokens: response.body.usage?.outputTokens,
        });
      } else if (response.status === 201) {
        toast.error(t('outputTemplates.workspace.generatePreviewFailed'));
      } else if (response.status === 422) {
        toast.error(
          response.body.message ??
            t('outputTemplates.workspace.generatePreviewFailed'),
        );
      } else {
        toast.error(t('outputTemplates.workspace.generatePreviewFailed'));
      }
    } catch (error) {
      toast.error(t('outputTemplates.workspace.generatePreviewFailed'), {
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const hasPreview = previewTemplate !== null;

  return (
    // minmax(0,…) so column ratios hold regardless of content min-width
    // (plain fr lets the preview toolbar's min-content win). No height
    // constraint: the grid must grow with the editor column so the sticky
    // preview panel has the full-height track to travel in.
    <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-2">
          <FieldLabel>
            {t('outputTemplates.workspace.templateNameLabel')}
          </FieldLabel>
          <Input
            placeholder={t('outputTemplates.workspace.templateNamePlaceholder')}
            value={templateName}
            onChange={(e) => onTemplateNameChange(e.target.value)}
            disabled={readOnly}
          />
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => sectionsEditorRef.current?.addSection()}
              disabled={readOnly}
              className="flex items-center gap-1.5 rounded-lg border border-border-primary px-3 py-1.5 text-sm font-medium text-text-secondary transition-colors hover:border-border-tertiary hover:bg-bg-secondary/60 hover:text-text-primary focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-border-primary disabled:hover:bg-transparent disabled:hover:text-text-secondary"
            >
              <Icon name="Plus" size="sm" />
              {t('outputTemplates.workspace.addSection')}
            </button>
            {allowGroups && (
              <button
                type="button"
                onClick={() => sectionsEditorRef.current?.addGroup()}
                disabled={readOnly}
                className="flex items-center gap-1.5 rounded-lg border border-border-primary px-3 py-1.5 text-sm font-medium text-text-secondary transition-colors hover:border-border-tertiary hover:bg-bg-secondary/60 hover:text-text-primary focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-border-primary disabled:hover:bg-transparent disabled:hover:text-text-secondary"
              >
                <Icon name="Layers" size="sm" />
                {t('outputTemplates.workspace.addGroup')}
              </button>
            )}
            <button
              type="button"
              onClick={() => setIsAdvancedOpen((open) => !open)}
              aria-expanded={isAdvancedOpen}
              className={cn(
                'flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none',
                isAdvancedOpen
                  ? 'border-border-tertiary bg-bg-secondary text-text-primary'
                  : 'border-border-primary text-text-secondary hover:border-border-tertiary hover:bg-bg-secondary/60 hover:text-text-primary',
              )}
            >
              <Icon name="Settings" size="sm" />
              {t('outputTemplates.workspace.additionalSettings')}
            </button>
            {isEluveAdmin && (
              <button
                type="button"
                onClick={() => setShowSchema((current) => !current)}
                aria-pressed={showSchema}
                className={cn(
                  'flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-none',
                  showSchema
                    ? 'border-border-tertiary bg-bg-secondary text-text-primary'
                    : 'border-border-primary text-text-secondary hover:border-border-tertiary hover:bg-bg-secondary/60 hover:text-text-primary',
                )}
              >
                <Icon name="Braces" size="sm" />
                {showSchema ? 'Show editor' : 'Show schema'}
              </button>
            )}
          </div>

          {/* Hidden rather than unmounted: the model slider's initial
              selection effect must run so the parent learns the default
              preset even if this panel is never opened */}
          <div
            className={cn(
              'flex flex-col gap-4 rounded-md border border-border-primary p-3',
              !isAdvancedOpen && 'hidden',
            )}
          >
            <ModelPresetSlider
              onPresetSelected={handlePresetSelected}
              initialModelArgsId={initialModelArgsId}
              disabled={readOnly}
            />
            <div className="flex flex-col gap-1.5">
              <FieldLabel>
                {t('outputTemplates.workspace.templateDescriptionLabel')}
              </FieldLabel>
              <Textarea
                rows={3}
                value={description}
                onChange={(event) => onDescriptionChange(event.target.value)}
                placeholder={t(
                  'outputTemplates.workspace.templateDescriptionPlaceholder',
                )}
                disabled={readOnly}
              />
            </div>
            <label className="flex cursor-pointer items-center justify-between gap-4">
              <div className="flex flex-col">
                <Text size="sm" weight="medium">
                  {t('outputTemplates.workspace.allowGroups')}
                </Text>
                <Text size="xs" color="tertiary">
                  {t('outputTemplates.workspace.allowGroupsDescription')}
                </Text>
              </div>
              <Switch
                checked={allowGroups}
                onCheckedChange={setAllowGroups}
                aria-label={t('outputTemplates.workspace.allowGroups')}
                disabled={readOnly}
              />
            </label>
          </div>

          <TemplateSectionsEditor
            key={editorKey}
            ref={sectionsEditorRef}
            initialDoc={initialDoc}
            templateName={templateName}
            showSchema={showSchema}
            editable={!readOnly}
            onDocChange={(doc) => {
              docRef.current = doc;
              onDocChange(doc);
            }}
          />
        </div>
      </div>

      {/* Sticky and capped to the visible scrollport (100svh minus Main's
          1.5rem margins + 4rem header + 2×2rem page padding) so the panel —
          and its Generate Preview button — stays on screen while the
          editor column scrolls. top-8 matches the page's p-8 inset. */}
      <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-md border border-border-primary lg:sticky lg:top-8 lg:h-[calc(100svh-9.5rem)] lg:self-start">
        <div className="flex items-center justify-between gap-2 border-b border-border-primary bg-bg-indigo-subtle-primary/50 px-3 py-2">
          <Text size="sm" weight="medium" className="shrink-0">
            {t('outputTemplates.common.preview')}
          </Text>
          <div className="flex min-w-0 items-center gap-1.5">
            <Select
              value={sampleId}
              onValueChange={handleSampleChange}
              disabled={isGenerating}
            >
              <SelectTrigger className="min-w-0 bg-bg-primary [&>span]:truncate">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SAMPLE_TRANSCRIPTS.map((sample) => (
                  <SelectItem key={sample.id} value={sample.id}>
                    {sample.label}
                  </SelectItem>
                ))}
                <SelectItem value={CUSTOM_SAMPLE_ID}>
                  {t('outputTemplates.workspace.customTranscriptOption')}
                </SelectItem>
              </SelectContent>
            </Select>
            {sampleId === CUSTOM_SAMPLE_ID && (
              <SimpleTooltip
                label={t('outputTemplates.workspace.editCustomTranscript')}
              >
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={t(
                    'outputTemplates.workspace.editCustomTranscript',
                  )}
                  disabled={isGenerating}
                  onClick={() => {
                    setCustomDraft(customTranscript);
                    setIsCustomDialogOpen(true);
                  }}
                >
                  <Icon name="Pencil" size="md" />
                </Button>
              </SimpleTooltip>
            )}
            <SimpleTooltip
              label={
                hasPreview
                  ? t('outputTemplates.workspace.regeneratePreviewTooltip')
                  : t('outputTemplates.workspace.generatePreviewTooltip')
              }
            >
              <Button
                variant="outline"
                size="sm"
                aria-label={
                  hasPreview
                    ? t('outputTemplates.workspace.regeneratePreviewTooltip')
                    : t('outputTemplates.workspace.generatePreviewTooltip')
                }
                onClick={handleGenerate}
                disabled={isGenerating || !selectedTranscript.trim()}
                className="bg-bg-primary"
              >
                <Icon
                  name="RefreshCw"
                  size="md"
                  className={isGenerating ? 'animate-spin' : undefined}
                />
              </Button>
            </SimpleTooltip>
          </div>
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-bg-indigo-subtle-primary/50 p-5">
          {isGenerating ? (
            statusMessages.length > 0 ? (
              <GenerationProgressLog messages={statusMessages} />
            ) : (
              <PreviewLoadingState />
            )
          ) : hasPreview ? (
            <>
              <TemplatePreviewArtifact
                blocks={previewTemplate.blocks}
                onEditSection={(key) =>
                  sectionsEditorRef.current?.scrollToSection(key)
                }
              />
              {previewStats && (
                <PreviewGenerationStats
                  durationMs={previewStats.durationMs}
                  inputTokens={previewStats.inputTokens}
                  outputTokens={previewStats.outputTokens}
                />
              )}
            </>
          ) : (
            // This branch only renders while not generating
            <PreviewEmptyState
              onGenerate={handleGenerate}
              disabled={!selectedTranscript.trim()}
            />
          )}
        </div>
      </div>

      <Dialog open={isCustomDialogOpen} onOpenChange={setIsCustomDialogOpen}>
        <DialogContent size="2xl" className="gap-4 p-6">
          <DialogHeader>
            <DialogTitle>
              {t('outputTemplates.workspace.customTranscriptTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('outputTemplates.workspace.customTranscriptDescription')}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={customDraft}
            onChange={(e) => setCustomDraft(e.target.value)}
            placeholder={t(
              'outputTemplates.workspace.customTranscriptPlaceholder',
            )}
            className="min-h-72 font-normal"
          />
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setIsCustomDialogOpen(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={saveCustomTranscript}
              disabled={!customDraft.trim()}
            >
              {t('outputTemplates.workspace.useTranscript')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
